import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import Logo from '../components/Logo';
import { countries } from '../data/countries';

function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    fullName: '',
    country: '',
    address: '',
    mobileNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    amount: '',
    investmentPlan: '',
    idCard: null,
    idCardUrl: null,
    idCardDeleteUrl: null,
    idCardId: null,
    dateOfBirth: '',
    homeAddress: '',
    employmentStatus: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'idCard' && files) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleIdCardUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('ID card image must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', `${formData.fullName}_ID_${Date.now()}`);

      const response = await fetch('https://api.imgbb.com/1/upload?key=5b3e260b02831873f7aeb434d1d64df3', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Store ID card information in formData state
        setFormData(prev => ({
          ...prev,
          idCardUrl: data.data.url,
          idCardDeleteUrl: data.data.delete_url,
          idCardId: data.data.id
        }));
        setError('');
      } else {
        setError('Failed to upload ID card. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading ID card:', error);
      setError('Error uploading ID card. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('\n'));
      setIsLoading(false);
      return;
    }

    // Validate ID card
    if (!formData.idCard && !formData.idCardUrl) {
      setError('Please upload your ID card');
      setIsLoading(false);
      return;
    }

    // Validate investment plan and amount
    if (!formData.investmentPlan) {
      setError('Please select an investment plan');
      setIsLoading(false);
      return;
    }

    // Validate amount based on selected plan
    const amount = parseFloat(formData.amount);
    switch(formData.investmentPlan) {
      case 'Standard Plan':
        if (amount < 1000 || amount > 4000) {
          setError('Standard Plan requires an investment between $1,000 and $4,000');
          setIsLoading(false);
          return;
        }
        break;
      case 'Premium Plan':
        if (amount < 5000 || amount > 9000) {
          setError('Premium Plan requires an investment between $5,000 and $9,000');
          setIsLoading(false);
          return;
        }
        break;
      case 'Advanced Plan':
        if (amount < 10000) {
          setError('Advanced Plan requires a minimum investment of $10,000');
          setIsLoading(false);
          return;
        }
        break;
      default:
        setError('Please select a valid investment plan');
      setIsLoading(false);
      return;
    }

    try {
      // Register the user
      const result = await register({
        ...formData,
        idCardUrl: formData.idCardUrl,
        idCardDeleteUrl: formData.idCardDeleteUrl,
        idCardId: formData.idCardId
      });

      // Store ID card information in Firestore
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        idCardUrl: formData.idCardUrl,
        idCardDeleteUrl: formData.idCardDeleteUrl,
        idCardId: formData.idCardId,
        idCardUploadDate: new Date().toISOString()
      });

      setRegistrationDetails({
        ...formData,
        password: undefined,
        confirmPassword: undefined,
        registrationDate: new Date().toISOString(),
        otpCodes: result.otpCodes,
        idCardUrl: formData.idCardUrl,
        idCardDeleteUrl: formData.idCardDeleteUrl,
        idCardId: formData.idCardId
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push('Password must be at least 8 characters long');
    if (!hasUpperCase) errors.push('Password must contain at least one uppercase letter');
    if (!hasLowerCase) errors.push('Password must contain at least one lowercase letter');
    if (!hasNumbers) errors.push('Password must contain at least one number');
    if (!hasSpecialChar) errors.push('Password must contain at least one special character');

    return errors;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircleIcon className="mx-auto h-16 w-16 text-primary-600" />
              </motion.div>
              <h2 className="mt-6 text-3xl font-bold text-gray-900">Registration Successful!</h2>
              <p className="mt-4 text-lg text-gray-600">
                Thank you for registering with Quickwealth Investment Group LLC.
              </p>
              
              {/* OTP Information */}
              <div className="mt-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Important Security Information</h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-md border border-primary-100">
                    <p className="font-medium text-gray-900 mb-2">Login OTP</p>
                    <p className="font-mono text-lg bg-primary-50 p-2 rounded">
                      {registrationDetails.otpCodes?.loginOTP}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-md border border-primary-100">
                    <p className="font-medium text-gray-900 mb-2">Withdrawal OTP</p>
                    <p className="font-mono text-lg bg-primary-50 p-2 rounded">
                      {registrationDetails.otpCodes?.withdrawalOTP}
                    </p>
                  </div>
                  <div className="mt-4 text-sm text-red-600">
                    <p>⚠️ Please save these OTP codes securely. They will be required for:</p>
                    <ul className="list-disc list-inside mt-2">
                      <li>Logging into your account</li>
                      <li>Processing withdrawals</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Registration Summary */}
              <div className="mt-8 bg-gray-50 rounded-lg p-6 text-left">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Registration Summary</h3>
                <div className="space-y-3">
                  <p className="text-gray-600">
                    <span className="font-medium">Name:</span> {registrationDetails.fullName}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Email:</span> {registrationDetails.email}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Investment Plan:</span> {registrationDetails.investmentPlan}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Investment Amount:</span> ${parseFloat(registrationDetails.amount).toLocaleString()}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Registration Date:</span> {new Date(registrationDetails.registrationDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <p className="text-gray-600">
                  We will review your application and send you:
                </p>
                <ul className="text-left max-w-md mx-auto space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Your account credentials
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Dashboard access instructions
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Investment plan details
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Next steps for account funding
                  </li>
                </ul>
              </div>

              <div className="mt-8 space-x-4">
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Return to Homepage
                  </motion.button>
                </Link>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-6 py-3 border border-primary-600 text-base font-medium rounded-md text-primary-600 hover:bg-primary-50"
                  >
                    Contact Support
                  </motion.button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="flex justify-center">
          <Logo darkMode={false} />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <div className="mt-1">
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="mt-1">
                <input
                  id="phone"
                  name="mobileNumber"
                  type="tel"
                  required
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <div className="mt-1">
                <select
                  id="country"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">Select your country</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>
              </div>

              {/* Investment Details */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Investment Details</h3>
                <div>
                  <label htmlFor="investmentPlan" className="block text-sm font-medium text-gray-700">
                    Investment Plan
                  </label>
                  <div className="mt-1">
                    <select
                      id="investmentPlan"
                      name="investmentPlan"
                      required
                      value={formData.investmentPlan}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="">Select an investment plan</option>
                      <option value="Standard Plan">Standard Plan ($1,000 - $4,000)</option>
                      <option value="Premium Plan">Premium Plan ($5,000 - $9,000)</option>
                      <option value="Advanced Plan">Advanced Plan ($10,000+)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                    Investment Amount ($)
                  </label>
                  <div className="mt-1">
                    <input
                      id="amount"
                      name="amount"
                      type="number"
                      required
                      value={formData.amount}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter investment amount"
                    />
                  </div>
                </div>

            <div>
              <label htmlFor="idCard" className="block text-sm font-medium text-gray-700">
                ID Card Image
              </label>
              <div className="mt-1">
                <input
                  id="idCard"
                  name="idCard"
                  type="file"
                  accept="image/*"
                  required
                      onChange={handleIdCardUpload}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Upload a clear image of your ID card (passport, driver's license, or national ID)
                </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                  Date of Birth
              </label>
              <div className="mt-1">
                <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                  required
                    value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
                <label htmlFor="employmentStatus" className="block text-sm font-medium text-gray-700">
                  Employment Status
                </label>
                <div className="mt-1">
                  <select
                    id="employmentStatus"
                    name="employmentStatus"
                    required
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">Select employment status</option>
                    <option value="Employed">Employed</option>
                    <option value="Self-Employed">Self-Employed</option>
                    <option value="Unemployed">Unemployed</option>
                    <option value="Retired">Retired</option>
                    <option value="Student">Student</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="homeAddress" className="block text-sm font-medium text-gray-700">
                Home Address
              </label>
              <div className="mt-1">
                <textarea
                  id="homeAddress"
                  name="homeAddress"
                  required
                  value={formData.homeAddress}
                  onChange={handleInputChange}
                  rows={3}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Password Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'Sign up'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Already have an account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup; 